import type { HTMLComponent } from "@commercekit/react";
import classNames from "classnames";
import type { FC } from "react";
import { useEffect, useMemo, useState } from "react";
import { usePrismicLabels } from "~/hooks/usePrismicLabel";

import type { ImageField, LinkField } from "@prismicio/client";
import { asText } from "@prismicio/helpers";
import { PrismicImage } from "@prismicio/react";
import { useGlobalCtx } from "~/store/globalState";

import type { LanguageModel } from "@commercekit/api";
import { useCheckoutSimple } from "@commercekit/remix/lib/hooks/useCheckout";
import { useCustomerSimple } from "@commercekit/remix/lib/hooks/useCustomer";
import { useLocalePath } from "@commercekit/remix/lib/hooks/useLocale";

import { useLocation } from "@remix-run/react";
import { SooqrSearchInput } from "~/components/atoms/SooqrSearchInput";
import WvLink from "~/components/atoms/WvLink";
import WvCustomerLogoutForm from "~/components/molecules/WvCustomerLogoutForm";
import { useCustomerMenuTabs } from "~/hooks/useCustomerMenuTabs";
import WvPopupShoppingCart from "~/components/molecules/WvPopupShoppingCart";
import type { LayoutDocumentDataLanguagesItem } from "~/lib/prismic";
import LanguageSelector from "./LanguageSelector";
import { StarRating } from "~/components/reviews";
import {
  formatRating,
  formatSteppedReviewCount,
} from "~/components/reviews/utils";
import type { MenuCategory } from "~/types/MenuCategory";

const Container: HTMLComponent = ({ children }) => (
  <div className="container h-100">{children}</div>
);

export const Mask: HTMLComponent<{ onMask?: () => void }> = ({
  onMask,
  className,
}) => (
  <div
    className={classNames(
      "fixed bottom-0 left-0 right-0 top-0 -z-10 bg-black opacity-60",
      className
    )}
    onClick={() => onMask?.()}
  />
);

const HeaderDesktopTopBar: FC<{
  logo: ImageField;
  logoLink: LinkField;
  onBehalf: string;
  languageNames: LayoutDocumentDataLanguagesItem[];
  languages: LanguageModel[];
  locale: string;
  sooqrAccount: string;
  headerRating?: { rating: number; reviewCount: number } | null;
}> = ({ logo, languageNames, languages, locale, sooqrAccount, headerRating }) => {
  const { hash, pathname } = useLocation();
  const { $pt } = usePrismicLabels();
  const localePath = useLocalePath();
  const { checkoutSimple, tempCart } = useCheckoutSimple();
  const steppedReviewCount = headerRating
    ? formatSteppedReviewCount(headerRating.reviewCount, locale)
    : "0+";

  const { customer } = useCustomerSimple();

  const toggleMenu = () => {
    if (document.body.classList.contains("nav--open")) {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      document.body.classList.remove("nav--open"),
        document
          .getElementsByClassName("wvheader__hamburger")[0]
          .classList.remove("menu-open");
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      document.body.classList.add("nav--open"),
        document
          .getElementsByClassName("wvheader__hamburger")[0]
          .classList.add("menu-open");
    }
  };

  const toggleMobileSearch = () => {
    const nav = document.querySelector(".wvheader");
    if (document.body.classList.contains("search-open")) {
      if (nav) nav.classList.remove("fixed");
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      document.body.classList.remove("search-open");
      document
        .getElementsByClassName("wvheader__search-container")[0]
        .classList.remove("active");
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      document.body.classList.add("search-open");
      document
        .getElementsByClassName("wvheader__search-container")[0]
        .classList.add("active");
      if (hash?.includes("sqr"))
        setTimeout(() => {
          if (nav && nav.getBoundingClientRect().bottom < nav.clientHeight) {
            nav.classList.add("fixed");
          }
        }, 500);
    }
  };

  const toggleMobileMenu = () => {
    document
      .getElementsByClassName("wvheader__hamburger")[0]
      .classList.remove("menu-open");
  };

  const closeSubMenu = () => {
    document.querySelector(".sqr-closeButton")?.click();
    document.body.classList.remove("nav--open");
  };

  const cart = tempCart ?? checkoutSimple;
  const cartCount = useMemo(() => {
    return (
      cart?.lineItems.reduce(
        (acc: number, item: { quantity: number }) => acc + item.quantity,
        0
      ) ?? 0
    );
  }, [cart?.lineItems]);

  useEffect(() => {
    const nav = document.querySelector(".wvheader");
    // Always close search on hash change
    if (document.body.classList.contains("search-open")) {
      if (nav) nav.classList.remove("fixed");
      document.body.classList.remove("search-open");
      document
        .getElementsByClassName("wvheader__search-container")[0]
        .classList.remove("active");
    }

    // Only open if hash contains sqr
    if (hash?.includes("sqr")) {
      document.body.classList.add("search-open");
      document
        .getElementsByClassName("wvheader__search-container")[0]
        .classList.add("active");
      setTimeout(() => {
        if (nav && nav.getBoundingClientRect().bottom < nav.clientHeight) {
          nav.classList.add("fixed");
        }
      }, 500);
    }
  }, [hash]);

  useEffect(() => {
    document.querySelector(".sqr-closeButton")?.click();
  }, [pathname]);

  return (
    <Container>
      <div className="row no-gutters align-items-center py-lg-3">
        <div className="col-auto d-lg-none">
          <a
            className="py-2 pr-3 d-block wvheader__hamburger"
            onClick={() => toggleMenu()}
          >
            <div className="wvheader__hamburger-icons">
              <svg width="32" height="25" xmlns="http://www.w3.org/2000/svg">
                <g fill="#FFF" fillRule="nonzero">
                  <path d="M30.667 13H1.333C.597 13 0 12.328 0 11.5S.597 10 1.333 10h29.334c.736 0 1.333.672 1.333 1.5s-.597 1.5-1.333 1.5zM30.667 3H1.333C.597 3 0 2.328 0 1.5S.597 0 1.333 0h29.334C31.403 0 32 .672 32 1.5S31.403 3 30.667 3zM30.667 23H1.333C.597 23 0 22.328 0 21.5S.597 20 1.333 20h29.334c.736 0 1.333.672 1.333 1.5s-.597 1.5-1.333 1.5z"></path>
                </g>
              </svg>

              <svg width="32" height="25" xmlns="http://www.w3.org/2000/svg">
                <g fill="#FFF" fillRule="nonzero">
                  <path d="M21.404 3.647L3.616 23.402c-.446.496-1.26.49-1.818-.012s-.648-1.312-.202-1.807L19.384 1.827c.446-.495 1.26-.49 1.818.012s.648 1.312.202 1.808z"></path>
                  <path d="M1.596 3.647l17.788 19.755c.446.496 1.26.49 1.818-.012s.648-1.312.202-1.807L3.616 1.827c-.446-.495-1.26-.49-1.818.012s-.648 1.312-.202 1.808z"></path>
                </g>
              </svg>
            </div>
          </a>
        </div>
        <div className="col d-flex flex-column flex-lg-row justify-content-center align-items-center justify-content-lg-start">
          <WvLink
            to={localePath("/")}
            className="d-flex align-items-center justify-content-center"
            onClick={() => {
              closeSubMenu();
            }}
          >
            <span className="flex flex-col items-center wvheader__logo flex-column">
              <PrismicImage field={logo} height={41} width={203} />
              <span className="mt-1 text-center wvheader__slogan d-block">
                {$pt("header_slogan")}
              </span>
            </span>
          </WvLink>
          <div className="wvheader__rating d-none d-lg-block ml-4_5">
            {headerRating && headerRating.rating > 0 ? (
              <WvLink to={localePath("/reviews/")}>
                <div className="wvheader__rating-stars">
                  <StarRating rating={headerRating.rating} size="lg" />
                </div>
                <div className="mt-1 wvheader__rating-count">
                  {$pt("header_reviews_dynamic_label", { rating: formatRating(headerRating.rating, locale || "nl-nl"), totalReviews: steppedReviewCount })}
                </div>
              </WvLink>
            ) : (
              <>
                <div className="wvheader__rating-stars">
                  <i data-icon-before=""></i>
                  <i data-icon-before=""></i>
                  <i data-icon-before=""></i>
                  <i data-icon-before=""></i>
                  <i data-icon-before=""></i>
                </div>
                <div className="mt-1 wvheader__rating-count">
                  {$pt("header_review_label")}
                </div>
              </>
            )}
          </div>
        </div>
        <div className="order-1 col-12 col-lg-auto order-lg-0 wvheader__search-container">
          <div className="mt-2 wvheader__search form-inline mr-lg-3 mt-lg-0">
            <div className="form-row flex-nowrap w-100">
              <div className="pl-0 pr-0 col pl-lg-3">
                <SooqrSearchInput locale={locale} sooqrAccount={sooqrAccount} />
              </div>
              <div className="col-auto pl-0 pr-0 ml-n1 mr-n1 mr-lg-0">
                <button
                  type="button"
                  className="btn form-control"
                  data-icon-after=""
                  aria-label="Search assortment..."
                ></button>
              </div>
            </div>
          </div>
        </div>
        <div className="col-auto">
          <a
            className="py-2 pl-3 wvheader__search d-lg-none"
            onClick={() => {
              closeSubMenu();
              toggleMobileMenu();
              toggleMobileSearch();
            }}
          >
            <svg width="23" height="23" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M22.719 21.364l-6.54-6.54a9.06 9.06 0 002.03-5.72C18.208 4.084 14.123 0 9.103 0 4.084 0 0 4.084 0 9.104s4.084 9.104 9.104 9.104a9.06 9.06 0 005.72-2.03l6.54 6.541a.956.956 0 001.355 0 .957.957 0 000-1.355zM9 16c-3.86 0-7-3.14-7-7s3.14-7 7-7 7 3.14 7 7-3.14 7-7 7z"
                fill="#FFF"
                fillRule="nonzero"
              ></path>
            </svg>
          </a>
        </div>
        <LanguageSelector
          languages={languages}
          languageNames={languageNames}
          locale={locale}
        />
        <div className="col-auto px-3 py-2 ml-2 text-center d-none d-lg-block">
          {customer ? (
            <WvLink
              to={localePath("/customer")}
              className="flex flex-col items-center wvheader__account"
            >
              <svg width="22" height="23" xmlns="http://www.w3.org/2000/svg">
                <g fill="#FFF" fillRule="nonzero">
                  <path d="M11.14 0C7.895 0 5.528 2.536 5.768 5.81c.227 3.106 2.624 5.69 5.374 5.69 2.748 0 5.141-2.583 5.373-5.69C16.756 2.574 14.372 0 11.141 0zm0 1.643c2.267 0 3.895 1.758 3.725 4.046-.172 2.3-1.904 4.168-3.724 4.168-1.822 0-3.556-1.87-3.724-4.167-.17-2.322 1.44-4.047 3.724-4.047zM10.781 12.938c-2.458 0-4.878.697-6.8 1.992-2.1 1.415-3.504 3.466-3.949 5.961C-.16 21.974.534 23 1.66 23h18.244c1.126 0 1.82-1.026 1.627-2.109-.445-2.495-1.85-4.546-3.95-5.961-1.92-1.295-4.341-1.992-6.799-1.992zm0 1.677c2.14 0 4.24.604 5.88 1.71 1.736 1.17 2.875 2.833 3.237 4.864l.002.013a.275.275 0 01-.005.118l-.001.003H1.669l-.001-.003a.286.286 0 01-.004-.131c.363-2.031 1.5-3.693 3.237-4.864 1.64-1.106 3.74-1.71 5.88-1.71z"></path>
                </g>
              </svg>

              <span className="d-block wvheader__icon-text">
                {$pt("profile_side_tab_account_name")}
              </span>
            </WvLink>
          ) : (
            <WvLink
              to={localePath("/customer/login")}
              className="flex flex-col items-center wvheader__account"
            >
              <svg width="22" height="23" xmlns="http://www.w3.org/2000/svg">
                <g fill="#FFF" fillRule="nonzero">
                  <path d="M11.14 0C7.895 0 5.528 2.536 5.768 5.81c.227 3.106 2.624 5.69 5.374 5.69 2.748 0 5.141-2.583 5.373-5.69C16.756 2.574 14.372 0 11.141 0zm0 1.643c2.267 0 3.895 1.758 3.725 4.046-.172 2.3-1.904 4.168-3.724 4.168-1.822 0-3.556-1.87-3.724-4.167-.17-2.322 1.44-4.047 3.724-4.047zM10.781 12.938c-2.458 0-4.878.697-6.8 1.992-2.1 1.415-3.504 3.466-3.949 5.961C-.16 21.974.534 23 1.66 23h18.244c1.126 0 1.82-1.026 1.627-2.109-.445-2.495-1.85-4.546-3.95-5.961-1.92-1.295-4.341-1.992-6.799-1.992zm0 1.677c2.14 0 4.24.604 5.88 1.71 1.736 1.17 2.875 2.833 3.237 4.864l.002.013a.275.275 0 01-.005.118l-.001.003H1.669l-.001-.003a.286.286 0 01-.004-.131c.363-2.031 1.5-3.693 3.237-4.864 1.64-1.106 3.74-1.71 5.88-1.71z"></path>
                </g>
              </svg>

              <span className="d-block wvheader__icon-text">
                {$pt("account_header_avatar_text")}
              </span>
            </WvLink>
          )}
        </div>

        <div className="col-auto py-2 pl-3 ml-2 text-center">
          <WvPopupShoppingCart
            popoverButton={
              <div className="wvheader__microcart">
                <div className="d-flex justify-content-center align-items-center">
                  <svg
                    width="25"
                    height="23"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g fill="#FFF" fillRule="nonzero">
                      <path d="M8.53 18.882a1.882 1.882 0 100 3.765 1.882 1.882 0 000-3.765zM21.706 18.882a1.882 1.882 0 100 3.765 1.882 1.882 0 000-3.765zM4.765.059c.45 0 .836.318.923.757l.004.02L8.378 16.06h14.27c.513 0 .93.411.94.922V17a.941.941 0 01-.922.941H7.588a.941.941 0 01-.923-.757l-.004-.02L3.975 1.94H1a.941.941 0 01-.941-.922V1C.059.486.47.069.98.059H4.765z"></path>
                      <path d="M23.673 3.864c.636-.205 1.338.338 1.21 1.063l-.005.022-1.694 8.47a.941.941 0 01-.902.757H7.588a.941.941 0 01-.018-1.882h13.92l1.317-6.588H5.706a.941.941 0 01-.941-.923v-.018c0-.514.411-.931.922-.941h17.705c.09 0 .178.012.263.037l.014.004h.004z"></path>
                    </g>
                  </svg>
                  {cart && cart?.lineItems.length > 0 && (
                    <span
                      className="wvheader__microcart-count"
                      data-count={cartCount || 0}
                    >
                      <span
                        className="d-block wvheader__microcart-number"
                        data-count={cartCount || 0}
                      >
                        {cartCount || 0}
                      </span>
                    </span>
                  )}
                </div>

                <span className="d-none d-lg-block wvheader__icon-text">
                  {$pt("header_cart_label")}
                </span>
              </div>
            }
            checkoutSimple={checkoutSimple}
            tempCart={tempCart}
          />
        </div>
      </div>
    </Container>
  );
};
const HeaderDesktopBottomBar: FC<{
  category: MenuCategory[];
  languageNames: LayoutDocumentDataLanguagesItem[];
  languages: LanguageModel[];
  locale: string;
}> = ({ category, languages, languageNames, locale }) => {
  const localePath = useLocalePath();

  const { customer } = useCustomerSimple();

  const { $pt } = usePrismicLabels();

  const closeSubMenu = () => {
    document.body.classList.remove("nav--open");
  };

  const isDesktop = () => window.matchMedia("(min-width: 992px)").matches;
  const mouseEnter = () => {
    if (isDesktop()) document.body.classList.add("nav--open");
  };
  const mouseLeave = () => {
    if (isDesktop()) document.body.classList.remove("nav--open");
  };

  const [lang, setLang] = useState(locale.split("-")[1]);

  if (lang != null) {
    languages = languages.filter((x) => x.defaultLang !== lang);
  }

  const getLanguageName = (code: string) => {
    const language = languageNames.find(
      (x) => asText(x.locale)?.toLowerCase() === code?.toLowerCase()
    )?.language;
    return language ? asText(language) : "";
  };

  const toggleMobileMenu = () => {
    document.querySelector(".sqr-closeButton")?.click();
    document
      .getElementsByClassName("wvheader__hamburger")[0]
      .classList.remove("menu-open");
  };

  return (
    <>
      <div className="wvheader__nav nav d-lg-flex">
        <div className="nav__main align-self-center col-lg">
          <div className="mx-0 row justify-content-center h-100">
            <div className="container">
              <ul
                className="mb-5 nav__items mb-lg-0"
                onMouseEnter={() => mouseEnter()}
                onMouseLeave={() => mouseLeave()}
              >
                {category &&
                  category.map((node, nodeIndex) => (
                    <li
                      key={`${node.id}_${nodeIndex}`}
                      className="nav__item desktop-last pr-lg-1 pr-xl-2 pr-xxl-4"
                    >
                      <WvLink
                        className="d-flex justify-content-between"
                        to={localePath(`${node.slug}`)}
                        onClick={() => {
                          closeSubMenu();
                          toggleMobileMenu();
                        }}
                      >
                        <span>{node.name}</span>
                        <span
                          className="nav__item-arrow"
                          data-icon-after=""
                        ></span>
                      </WvLink>
                      <div className="nav__subitems-container nav__subitem--active py-lg-3">
                        <div className="container px-0 h-100">
                          <div className="row no-gutters h-100">
                            <div className="px-3 col-12 px-lg-0">
                              {node.children?.length ? (
                                <ul className="nav__subitems" data-subs="big">
                                  {node.children.map(
                                    (nodeChild, nodeChildIndex) => (
                                      <li
                                        key={`${nodeChild.id}_${nodeChildIndex}`}
                                        className="nav__subitem nav__subitem--active nav__subitem--subs col-lg px-lg-1 px-xl-2"
                                      >
                                        <WvLink
                                          className="d-flex justify-content-start"
                                          to={localePath(`${nodeChild.slug}`)}
                                          onClick={() => {
                                            closeSubMenu();
                                            toggleMobileMenu();
                                          }}
                                        >
                                          <span className="nav__subitem--header">
                                            {nodeChild.name}
                                          </span>
                                        </WvLink>

                                        <div className="nav__subsubitems-container">
                                          <div className="row no-gutters">
                                            <div className="px-3 col-12 px-lg-0">
                                              {nodeChild.children?.length ? (
                                                <ul className="nav__subsubitems mb-lg-2">
                                                  {nodeChild.children.map(
                                                    (
                                                      nodeChildChild,
                                                      nodeChildChildIndex
                                                    ) => (
                                                      <li
                                                        key={`${nodeChildChild.id}_${nodeChildChildIndex}`}
                                                        className="nav__subsubitem "
                                                      >
                                                        <WvLink
                                                          className="d-flex justify-content-between"
                                                          to={localePath(
                                                            `${nodeChildChild.slug}`
                                                          )}
                                                          onClick={() => {
                                                            closeSubMenu();
                                                            toggleMobileMenu();
                                                          }}
                                                        >
                                                          <span>
                                                            {
                                                              nodeChildChild.name
                                                            }
                                                          </span>
                                                        </WvLink>
                                                      </li>
                                                    )
                                                  )}
                                                </ul>
                                              ) : null}
                                            </div>
                                            <WvLink
                                              to={localePath(
                                                `${nodeChild.slug}`
                                              )}
                                              className="link d-none d-lg-block"
                                              data-icon-after=""
                                            >
                                              {$pt("header_menu_all_label")}{" "}
                                              {nodeChild.name}
                                            </WvLink>
                                          </div>
                                        </div>
                                      </li>
                                    )
                                  )}
                                </ul>
                              ) : null}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => closeSubMenu()}
                          className="nav__close"
                          data-icon-before=""
                        ></button>
                      </div>
                    </li>
                  ))}
                {/* <li className="nav__item nav__item--sale pl-lg-1 pl-xl-2 pl-xxl-4">
                  <WvLink
                    className="d-flex justify-content-between px-lg-2 d-lg-none d-xl-flex"
                    to={localePath("sales")}
                  >
                    <span>{$pt("header_sale_label")}</span>
                  </WvLink>
                </li> */}
                <li className="nav__item nav__item--account d-lg-none">
                  {customer ? (
                    <MobileAccountMenu
                      firstName={customer.firstName}
                      onNavigate={() => {
                        closeSubMenu();
                        toggleMobileMenu();
                      }}
                    />
                  ) : (
                    <WvLink
                      to={localePath("/customer/login")}
                      className="d-flex justify-content-between"
                      onClick={() => {
                        closeSubMenu();
                        toggleMobileMenu();
                      }}
                    >
                      <span data-icon-before="">
                        {$pt("header_account_label")}
                      </span>
                      <span
                        className="nav__item-arrow"
                        data-icon-after=""
                      ></span>
                    </WvLink>
                  )}
                </li>
              </ul>
              <ul className="nav__items nav__languages d-lg-none">
                {languages &&
                  languages.map((item) => (
                    <li
                      key={`${item.country}-${item.defaultLang}`}
                      className="nav__item"
                    >
                      <a
                        href={`https://${item.domain}`}
                        target="_blank"
                        className="d-flex justify-content-between"
                        onClick={() => {
                          setLang(lang);
                        }}
                        rel="noopener noreferrer"
                      >
                        <span className="d-flex align-items-center">
                          <div
                            className={classNames(
                              "fflag ff-md mr-3",
                              `fflag-${item?.defaultLang.toUpperCase()}`
                            )}
                          ></div>

                          <span> {getLanguageName(item.defaultLang)}</span>
                        </span>
                        <span data-icon-after=""></span>
                      </a>
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const MobileAccountMenu: FC<{
  firstName?: string | null;
  onNavigate: () => void;
}> = ({ firstName, onNavigate }) => {
  const { $pt } = usePrismicLabels();
  const localePath = useLocalePath();
  const tabs = useCustomerMenuTabs();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      if (!document.body.classList.contains("nav--open")) setOpen(false);
    });
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <a
        href={localePath("/customer")}
        role="button"
        className="d-flex justify-content-between"
        aria-expanded={open}
        aria-controls="mobile-account-sheet"
        onClick={(event) => {
          event.preventDefault();
          setOpen(true);
        }}
      >
        <span data-icon-before={"\uf207"}>
          {$pt("profile_side_tab_account_name")}
        </span>
        <span className="nav__item-arrow" data-icon-after={"\uf2fb"}></span>
      </a>
      <div
        id="mobile-account-sheet"
        className={classNames("account-sheet d-lg-none", { open })}
        aria-hidden={!open}
      >
        <button
          type="button"
          className="account-sheet__back"
          tabIndex={open ? 0 : -1}
          onClick={() => setOpen(false)}
        >
          <span
            className="nav__item-arrow account-sheet__back-icon"
            data-icon-after={"\uf2fb"}
            aria-hidden
          ></span>
          {$pt("back")}
        </button>
        <WvLink
          to={localePath("/customer")}
          className="account-sheet__title"
          tabIndex={open ? 0 : -1}
          onClick={() => {
            setOpen(false);
            onNavigate();
          }}
        >
          {$pt("account_greeting").trim()}, {firstName}
        </WvLink>
        <ul className="nav__items">
          {tabs.map((tab) => (
            <li key={tab.link} className="nav__item">
              <WvLink
                to={localePath(tab.link)}
                tabIndex={open ? 0 : -1}
                onClick={() => {
                  setOpen(false);
                  onNavigate();
                }}
              >
                {tab.name}
              </WvLink>
            </li>
          ))}
        </ul>
        <WvCustomerLogoutForm className="px-3 mt-4" />
      </div>
    </>
  );
};

const HeaderDesktop: FC<{
  logo: ImageField;
  category: MenuCategory[];
  logoLink: LinkField;
  title: string;
  onBehalf: string;
  languageNames: LayoutDocumentDataLanguagesItem[];
  languages: LanguageModel[];
  locale: string;
  sooqrAccount: string;
  headerRating?: { rating: number; reviewCount: number } | null;
}> = ({
  logo,
  category,
  logoLink,
  onBehalf,
  languageNames,
  languages,
  locale,
  sooqrAccount,
  headerRating,
}) => {
    const {
      isCheckoutHeaderLayout,
      state: { openMenuId },
    } = useGlobalCtx();

    return (
      <div
        style={{ display: isCheckoutHeaderLayout ? "none" : "block" }}
        className="w-full"
      >
        {openMenuId !== "" ? <Mask className="hidden lg:block" /> : null}
        <HeaderDesktopTopBar
          onBehalf={onBehalf}
          logoLink={logoLink}
          logo={logo}
          languages={languages}
          languageNames={languageNames}
          locale={locale}
          sooqrAccount={sooqrAccount}
          headerRating={headerRating}
        />
        <HeaderDesktopBottomBar
          category={category}
          languages={languages}
          languageNames={languageNames}
          locale={locale}
        />
      </div>
    );
  };

export default HeaderDesktop;
